import { Check } from "lucide-react";

/**
 * Stepper — a calm, motion-aware progress line.
 *
 * Visual:
 *  - Continuous track (light border) with a lime fill segment representing
 *    current progress.
 *  - Numbered nodes for each step that flip to a lime check icon once complete.
 *  - Per-step label below each node, with the active label coloured charcoal.
 *  - Aria attributes for screen readers.
 *
 * Props:
 *  steps: [{ id, label }]
 *  current: 1-indexed active step
 */
export const Stepper = ({ steps = [], current = 1 }) => {
  const total = steps.length;
  const progressPct =
    total <= 1 ? 100 : ((current - 1) / (total - 1)) * 100;

  return (
    <div
      className="w-full"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current}
      data-testid="step-indicator"
    >
      <div className="relative">
        {/* Track */}
        <div className="absolute left-0 right-0 top-4 h-1 bg-[var(--light-border)] rounded-full" />
        {/* Filled progress */}
        <div
          className="absolute left-0 top-4 h-1 bg-[var(--lime)] rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
        {/* Nodes */}
        <ol className="relative flex justify-between">
          {steps.map((step) => {
            const isComplete = step.id < current;
            const isActive = step.id === current;
            return (
              <li
                key={step.id}
                className="flex flex-col items-center"
                aria-current={isActive ? "step" : undefined}
              >
                <span
                  className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-300 ${
                    isComplete
                      ? "bg-[var(--lime)] border-[var(--lime)] text-[var(--dark-bg)]"
                      : isActive
                      ? "bg-white border-[var(--lime)] text-[var(--charcoal)] scale-110 shadow-[0_0_0_4px_rgba(196,247,75,0.18)]"
                      : "bg-white border-[var(--light-border)] text-[var(--text-muted)]"
                  }`}
                >
                  {isComplete ? <Check size={16} strokeWidth={3} /> : step.id}
                </span>
                <span
                  className={`mt-3 hidden sm:block text-xs font-medium tracking-tight transition-colors duration-300 ${
                    isActive
                      ? "text-[var(--charcoal)]"
                      : isComplete
                      ? "text-[var(--charcoal)]/70"
                      : "text-[var(--text-muted)]"
                  }`}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      {/* Mobile label of just the current step */}
      <p className="sm:hidden text-center mt-3 text-sm font-semibold text-[var(--charcoal)]">
        Step {current} of {total}
        <span className="text-[var(--text-muted)] font-normal">
          {" "}· {steps.find((s) => s.id === current)?.label}
        </span>
      </p>
    </div>
  );
};

export default Stepper;
